import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import StripePricingTable from '../components/stripe/StripePricingTable';
import { LandingHeader } from '../components/layout/landing-header';
import { PRIVATE_BETA_ENABLED } from '../config/api';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  // Show private beta message if enabled
  if (PRIVATE_BETA_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <LandingHeader currentPage="pricing" />
        
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full text-sm font-medium shadow-lg">
              <Sparkles className="h-4 w-4" />
              <span>Private Beta</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Pricing Coming Soon
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              We're currently in private beta. Join our waitlist to get early access 
              and special beta pricing when we launch.
            </p>
            
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-4">What to expect:</h2>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Transparent, usage-based pricing</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Free tier with 100 conversations/month</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Special discounts for beta users</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>No credit card required during beta</span>
                </li>
              </ul>
            </div>
            
            <Button 
              size="lg" 
              onClick={() => navigate('/start')}
              className="inline-flex items-center gap-2"
            >
              Join the Waitlist
              <ArrowLeft className="h-5 w-5 rotate-180" />
            </Button>
            
            <p className="mt-6 text-sm text-gray-500">
              Questions about pricing? <a href="/contact" className="text-blue-600 hover:underline">Contact us</a>
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <LandingHeader currentPage="pricing" />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the perfect plan for your business. All plans include our core chatbot features,
            with limits that scale as you grow.
          </p>
        </div>

        {/* Plan Features Overview */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-2">Free Plan</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>10 conversations/day</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>10 pages max</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Basic chatbot</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-2">Starter €99/mo</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>100 conversations/day</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>50 pages max</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email support</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-blue-500">
              <h3 className="font-semibold text-lg mb-2">Growth €299/mo</h3>
              <div className="text-xs text-blue-600 font-semibold mb-2">MOST POPULAR</div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>500 conversations/day</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>500 pages max</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Voice bot included</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>API access</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-lg mb-2">Pro €599+/mo</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>2,000 conversations/day</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unlimited pages</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>All features</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Dedicated support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Stripe Pricing Table */}
        <div className="w-full max-w-6xl mx-auto">
          <StripePricingTable />
        </div>

        {/* FAQs */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-lg mb-2">What counts as a conversation?</h4>
              <p className="text-gray-600">
                A conversation is any interaction where a user sends a message to your chatbot and receives a response.
                Each new message from a user counts as one conversation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Can I change plans anytime?</h4>
              <p className="text-gray-600">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately,
                and we'll prorate the difference.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">What happens if I exceed my limits?</h4>
              <p className="text-gray-600">
                If you reach your daily conversation limit, the chatbot will show a friendly message to users.
                You can upgrade your plan to immediately increase your limits.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Do you offer enterprise plans?</h4>
              <p className="text-gray-600">
                Yes! For businesses needing more than 2,000 conversations per day or custom features,
                please <a href="/contact" className="text-blue-600 hover:underline">contact our sales team</a>.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;